import { defineStore } from 'pinia'
import ClassificationTypeResource, {
  type IClassificationType,
  type IClassificationTypeGroup,
} from '@/api/resources/classificationType'
import { useSuccessStore } from './success'
import { useAlarmStore } from './alarm'

export const useClassificationTypeStore = defineStore({
  id: 'classificationType',

  state: () => ({
    types: [] as IClassificationType[],
    groups: [] as IClassificationTypeGroup[],
  }),

  getters: {
    listTypes: (state) => state.types,
    listGroups: (state) => state.groups,
  },

  actions: {
    async fetchTypes({ force = false } = {}) {
      if (this.types.length === 0 || force) {
        try {
          const data = await ClassificationTypeResource.getTypes()
          this.types = Array.isArray(data) ? data : []
        } catch {
          this.types = []
        }
      }
    },

    async fetchGroups({ force = false } = {}) {
      try {
        const data = await ClassificationTypeResource.getGroups()
        this.groups = Array.isArray(data) ? data : []
      } catch {
        this.groups = []
      }
    },

    async createType(name: string, nature: string | null = null) {
      const created = await ClassificationTypeResource.createType({ name, nature: nature || null })
      this.types.push(created)
      useSuccessStore().setSuccess(['Tipo de classificação criado com sucesso!'])
      useAlarmStore().fetchAlarmClassifications({ force: true })
      return created
    },

    async updateType(id: number, name: string, nature: string | null = null) {
      const updated = await ClassificationTypeResource.updateType(id, { name, nature: nature || null })
      const index = this.types.findIndex((t) => t.id === id)
      if (index !== -1) {
        this.types[index] = { ...this.types[index], ...updated }
      }
      useSuccessStore().setSuccess(['Tipo de classificação atualizado com sucesso!'])
      useAlarmStore().fetchAlarmClassifications({ force: true })
      return updated
    },

    async deleteType(id: number) {
      await ClassificationTypeResource.deleteType(id)
      this.types = this.types.filter((t) => t.id !== id)
      useSuccessStore().setSuccess(['Tipo de classificação removido com sucesso!'])
      useAlarmStore().fetchAlarmClassifications({ force: true })
    },

    async createGroup(params: {
      name: string
      classification_type_ids?: number[]
      sensor_ids?: number[]
      group_ids?: number[]
    }) {
      const created = await ClassificationTypeResource.createGroup(params)
      this.groups.push(created)
      useSuccessStore().setSuccess(['Grupo de classificação criado com sucesso!'])
      return created
    },

    async updateGroup(
      id: number,
      params: {
        name: string
        classification_type_ids?: number[]
        sensor_ids?: number[]
        group_ids?: number[]
      },
    ) {
      const updated = await ClassificationTypeResource.updateGroup(id, params)
      const index = this.groups.findIndex((g) => g.id === id)
      if (index !== -1) {
        this.groups[index] = { ...this.groups[index], ...updated }
      }
      useSuccessStore().setSuccess(['Grupo de classificação atualizado com sucesso!'])
      return updated
    },

    async deleteGroup(id: number) {
      await ClassificationTypeResource.deleteGroup(id)
      this.groups = this.groups.filter((g) => g.id !== id)
      useSuccessStore().setSuccess(['Grupo de classificação removido com sucesso!'])
    },
  },
})
